import * as Yup from 'yup'

import type { VerifyOtpResponse } from 'models/response'
import { trueErrorMessage } from 'utils/error'
import { parseFutureDate } from 'utils/invite-accept'
import { TURNSTILE_ERROR_CODES, TURNSTILE_FAILED_MESSAGE, retryAfterToDate } from 'utils/phone-accounts'

export const EMAIL_CODE_LENGTH = 6

export const INVALID_EMAIL_MESSAGE = 'Enter a valid email address.'
export const START_RATE_LIMITED_MESSAGE = 'Too many requests. Please wait a moment and try again.'
export const VERIFY_RATE_LIMITED_MESSAGE = 'Too many attempts. Please wait a moment and try again.'
export const CODE_EXPIRED_MESSAGE = 'This code has expired or was already used. Resend a new code.'
export const CODE_LOCKED_MESSAGE =
	'Too many wrong codes. Code sign-in for this email is paused for 30 minutes. You can continue with your password.'
export const NO_PASSWORD_MESSAGE = "This account doesn't use a password — enter the code we emailed you."
export const CODE_RESENT_MESSAGE = 'We sent you a new code.'
/** Under the "Forgot your password?" heading */
export const FORGOT_PASSWORD_MESSAGE = "We'll email you a code to sign in."

/** The verify refusal for a right code when no account uses the email */
export const NO_ACCOUNT_CODE = '401_User_001'

const emailSchema = Yup.string().trim().email().required()

/** Typed values with an "@" go to the code flow; anything else is a phone number or a username. */
export const looksLikeEmail = (value?: string | null): boolean => !!value && value.includes('@')

export const isValidEmail = (value?: string | null): boolean => emailSchema.isValidSync(value)

export const invalidCodeMessage = (attemptsLeft: number): string => {
	if (attemptsLeft <= 0) return 'Invalid or expired code.'
	return `Invalid or expired code. ${attemptsLeft} ${attemptsLeft === 1 ? 'attempt' : 'attempts'} left.`
}

export type EmailStartFailure = { kind: 'rateLimited'; retryAt: Date | null } | { kind: 'error'; message: string }

/**
 * `error` is the axios response the `api` instance rejects with. Never says anything about the account:
 * start answers the same whether or not the address is registered.
 */
export function interpretEmailStartError(error: any, now: number = Date.now()): EmailStartFailure {
	if (TURNSTILE_ERROR_CODES.has(error?.data?.code)) return { kind: 'error', message: TURNSTILE_FAILED_MESSAGE }
	if (error?.status === 429) {
		// TooManyRequestsIpRateLimit may carry Retry-After; the resend throttle (TooManyRequestsOtp) never does
		const headers = error.headers ?? {}
		const header = headers['retry-after'] ?? headers['Retry-After']
		return { kind: 'rateLimited', retryAt: header ? retryAfterToDate(header, now) : null }
	}
	if (error?.status === 400) return { kind: 'error', message: INVALID_EMAIL_MESSAGE }
	return { kind: 'error', message: trueErrorMessage(error?.data) }
}

export type EmailVerifyFailure =
	| { kind: 'wrong'; attemptsLeft: number }
	| { kind: 'locked'; blockedUntil: Date }
	/** No live code for this address: expired, already used, or replaced by a newer one. */
	| { kind: 'expired' }
	| { kind: 'rateLimited' }
	/** 401_User_001: the code was right, but no account uses this email. The code is spent. */
	| { kind: 'noAccount' }
	| { kind: 'error'; message: string }

/** A refused code is a VerifyOtpResult; an error code in the body means something else refused the request. */
export const isVerifyOtpResponse = (body: unknown): body is VerifyOtpResponse =>
	typeof body === 'object' && body !== null && typeof (body as VerifyOtpResponse).result === 'string'

export function interpretEmailVerifyError(error: any, now: number = Date.now()): EmailVerifyFailure {
	if (error?.status === 429) return { kind: 'rateLimited' }
	const body = error?.data
	// Through the API's legacy error path, which drops the code from a plain-text body: told apart by status (or the code)
	if (error?.status === 401 || body?.code === NO_ACCOUNT_CODE) return { kind: 'noAccount' }
	// Plain-text 400s (malformed email, blank code) have no VerifyOtpResult body
	if (error?.status !== 400 || !isVerifyOtpResponse(body)) {
		return { kind: 'error', message: trueErrorMessage(body) }
	}
	// The third wrong code resets failedAttempts to 0 and sets blockedUntil, so the lockout is read first
	const blockedUntil = parseFutureDate(body.blockedUntil, now)
	if (blockedUntil) return { kind: 'locked', blockedUntil }
	if (body.result === 'Failed') {
		return { kind: 'wrong', attemptsLeft: Math.max(0, (body.maxAttempts ?? 0) - (body.failedAttempts ?? 0)) }
	}
	return { kind: 'expired' }
}

/**
 * password/sign-in refusing an account with no password (BadRequestAccountDoNotHavePassword*, 400_User_023[_n]).
 * Those errors take the legacy plain-text path, which drops the code from the body, so the message is matched too.
 */
const NO_PASSWORD_ERROR =
	/400_User_023|please set up a password for your account|this account was created with (facebook|apple|google|twitter)/i

export const isNoPasswordSignInError = (message?: string | null): boolean =>
	!!message && NO_PASSWORD_ERROR.test(message)
